import { SubscriptionStatusEnum } from "../../data-structures/enums";
import type { Subscription } from "../../data-structures/interfaces";

export enum SubscriptionDisplayView {
    Current = "current",
    Archived = "archived",
    All = "all",
}

export interface SubscriptionDisplayViewCounts {
    current: number;
    archived: number;
    all: number;
}

const toDate = (value?: Date | string | null): Date | null => {
    if (!value) {
        return null;
    }

    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

export const isCurrentSubscription = (subscription?: Pick<Subscription, "status" | "expiryDateRaw" | "expiryDate">, now: Date = new Date()): boolean => {
    if (!subscription) {
        return false;
    }

    const status = subscription.status?.toLowerCase();
    const expiryDate = toDate(subscription.expiryDateRaw ?? subscription.expiryDate);

    if (status === SubscriptionStatusEnum.Pending) {
        return true;
    }

    // Suspended is recoverable and time-bounded (Stripe dunning / past_due, marketplace
    // 30-day reinstate grace) — the most actionable state an admin can still save. Keep
    // it in Current regardless of ExpiryDate; the terminal transition (Reinstate/Cancel/
    // Complete) is what moves it out, not a date.
    if (status === SubscriptionStatusEnum.Suspended) {
        return true;
    }

    if (status === SubscriptionStatusEnum.Active) {
        return !expiryDate || expiryDate >= now;
    }

    if (status === SubscriptionStatusEnum.Canceled) {
        return !!expiryDate && expiryDate >= now;
    }

    return false;
};

export const filterSubscriptionsByDisplayView = <T extends Pick<Subscription, "status" | "expiryDateRaw" | "expiryDate">>(
    items: T[],
    view: SubscriptionDisplayView,
    now: Date = new Date()
): T[] => {
    if (view === SubscriptionDisplayView.All) {
        return items;
    }

    return items.filter(item => {
        const isCurrent = isCurrentSubscription(item, now);
        return view === SubscriptionDisplayView.Current ? isCurrent : !isCurrent;
    });
};

export const getSubscriptionDisplayViewCounts = <T extends Pick<Subscription, "status" | "expiryDateRaw" | "expiryDate">>(
    items: T[],
    now: Date = new Date()
): SubscriptionDisplayViewCounts => {
    const current = items.filter(item => isCurrentSubscription(item, now)).length;

    return {
        current,
        archived: items.length - current,
        all: items.length,
    };
};
